import React, { useRef, useState, DragEvent } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import { useCanvasStore, Tool } from '@/stores/canvasStore';
import { useArrowStore } from '@/stores/arrowStore';

import { supabase } from '@/integrations/supabase/client';
import { fetchLinkPreview, linkCardHeight } from '@/lib/linkPreview';
import { offerPullPost } from '@/extensions/creative-scout/pullPost';
import {
  MousePointer2,
  Hand,
  Folder,
  StickyNote,
  Type,
  FileText,
  Image,
  File,
  Link2,
  Loader2,
  MoveRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PartnrSearchBar } from '@/components/partnr/PartnrSearchBar';
import { LinkInputModal } from '@/components/modals/LinkInputModal';
import { ExtensionsLauncher } from '@/components/extensions/ExtensionsLauncher';
import { compressImage } from '@/lib/imageCompression';
import { normalizeImageFile } from '@/lib/tiffImage';
import { isExtractableFile, requestFileExtraction } from '@/lib/fileExtraction';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';
import { useDockMagnify } from './useDockMagnify';
import { DockGlyph } from './DockGlyph';

interface ToolButton {
  tool: Tool;
  icon: React.ElementType;
  label: string;
  shortcut?: string;
  isFileUpload?: boolean;
  accept?: string;
  isModal?: boolean;
  isDraggable?: boolean;
  isArrowTool?: boolean;
}

const tools: ToolButton[] = [
  { tool: 'select', icon: MousePointer2, label: 'Select', shortcut: 'V' },
  { tool: 'pan', icon: Hand, label: 'Pan', shortcut: 'H' },
  { tool: 'board', icon: Folder, label: 'Add Board', shortcut: 'B', isDraggable: true },
  { tool: 'sticky', icon: StickyNote, label: 'Add Sticky Note', shortcut: 'S', isDraggable: true },
  { tool: 'text_card', icon: Type, label: 'Add Text Card', shortcut: 'T', isDraggable: true },
  { tool: 'document', icon: FileText, label: 'Add Document', shortcut: 'D', isDraggable: true },
  { tool: 'arrow', icon: MoveRight, label: 'Arrow/Connector', shortcut: 'A', isArrowTool: true },
  { tool: 'link', icon: Link2, label: 'Add Link', shortcut: 'L', isModal: true },
  { tool: 'image', icon: Image, label: 'Add Image', isFileUpload: true, accept: 'image/*' },
  { tool: 'file', icon: File, label: 'Add File', isFileUpload: true },
];

export function BottomToolbar() {
  const rightInset = usePartnrPanelStore((s) => s.rightInset);
  const { 
    activeTool, 
    setActiveTool, 
    currentBoardId, 
    createItem, 
    viewMode, 
    setDraggingFromToolbar,
  } = useCanvasStore();
  const isArrowMode = useArrowStore((s) => s.isArrowMode);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const dock = useDockMagnify<HTMLDivElement>();

  // Only show toolbar in Focus mode with a board selected
  if (viewMode !== 'focus' || !currentBoardId) return null;

  // Check if a file is an image based on mime type
  const isImageFile = (file: File): boolean => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    return imageTypes.includes(file.type);
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'image' | 'file'
  ) => {
    const picked = e.target.files?.[0];
    if (!picked || !currentBoardId) return;

    setIsUploading(true);

    try {
      // TIFF -> PNG: browsers can't draw TIFF
      const file = await normalizeImageFile(picked);

      // Get user for secure file path
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to upload files.');
        setIsUploading(false);
        return;
      }

      let fileToUpload = file;
      
      // Compress images before upload
      const shouldBeImage = type === 'image' || isImageFile(file);
      if (shouldBeImage) {
        const result = await compressImage(file);
        fileToUpload = result.file;
      }
      
      // User-scoped file path for RLS compliance
      const filePath = `${user.id}/${currentBoardId}/${Date.now()}_${file.name}`;
      const { data, error } = await supabase.storage
        .from('uploads')
        .upload(filePath, fileToUpload);

      if (error) throw error;

      // Use signed URL instead of public URL for private bucket
      const { data: signedUrlData, error: urlError } = await supabase.storage
        .from('uploads')
        .createSignedUrl(filePath, 31536000); // 1 year expiry

      if (urlError || !signedUrlData) throw new Error('Failed to get file URL');
      const fileUrl = signedUrlData.signedUrl;

      const x = window.innerWidth / 2 - 100;
      const y = window.innerHeight / 2 - 100;

      if (shouldBeImage) {
        const img = new window.Image();
        img.onload = async () => {
          const maxWidth = 400;
          const aspectRatio = img.width / img.height;
          const width = Math.min(img.width, maxWidth);
          const height = width / aspectRatio;
          
          await createItem({
            board_id: currentBoardId,
            item_type: 'image',
            position_x: x,
            position_y: y,
            width: Math.round(width / 20) * 20,
            height: Math.round(height / 20) * 20,
            content: { url: fileUrl, alt: file.name, storagePath: filePath },
          });
          
          setIsUploading(false);
        };
        img.onerror = async () => {
          await createItem({
            board_id: currentBoardId,
            item_type: 'image',
            position_x: x,
            position_y: y,
            width: 300,
            height: 200,
            content: { url: fileUrl, alt: file.name, storagePath: filePath },
          });
          setIsUploading(false);
        };
        img.src = fileUrl;
      } else {
        const createdFile = await createItem({
          board_id: currentBoardId,
          item_type: 'file',
          position_x: x,
          position_y: y,
          width: 220,
          height: 100,
          content: { url: fileUrl, filename: file.name, size: file.size, mimeType: file.type, storagePath: filePath },
        });
        // Read the text in the background so the file is searchable and Partnr can answer from it
        if (createdFile && isExtractableFile(file.name, file.type, file.size)) {
          requestFileExtraction(createdFile.id);
        }
        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Upload failed. Please try again.');
      setIsUploading(false);
    }

    e.target.value = '';
  };

  const handleAddLink = async (url: string) => {
    if (!currentBoardId) {
      toast.error('Please navigate to a board first.');
      return;
    }
    
    setIsAddingLink(true);
    
    try {
      // Never fails on the preview: a blocked or slow fetch (Instagram, TikTok)
      // still adds a card built from the URL.
      const content = await fetchLinkPreview(url);

      const x = window.innerWidth / 2 - 150;
      const y = window.innerHeight / 2 - 100;

      const result = await createItem({
        board_id: currentBoardId,
        item_type: 'link',
        position_x: x,
        position_y: y,
        width: 380,
        height: linkCardHeight(content),
        content: content as unknown as Record<string, unknown>,
      });

      if (result) {
        setShowLinkModal(false);
        offerPullPost(result);
      }
    } finally {
      setIsAddingLink(false);
    }
  };

  const handleToolClick = (tool: Tool, isFileUpload?: boolean, isModal?: boolean, isArrowTool?: boolean) => {
    if (isFileUpload) {
      if (tool === 'image') imageInputRef.current?.click();
      else fileInputRef.current?.click();
    } else if (isModal && tool === 'link') {
      setShowLinkModal(true);
    } else if (isArrowTool) {
      // Toggle arrow mode
      const { isArrowMode, setArrowMode } = useArrowStore.getState();
      if (isArrowMode) {
        setArrowMode(false);
        setActiveTool('select');
      } else {
        setArrowMode(true);
        setActiveTool('arrow');
      }
    } else {
      // Exit arrow mode when selecting other tools
      const { isArrowMode, setArrowMode } = useArrowStore.getState();
      if (isArrowMode) {
        setArrowMode(false);
      }
      setActiveTool(tool);
    }
  };
  
  const handleDragStart = (e: DragEvent<HTMLButtonElement>, tool: Tool) => {
    e.dataTransfer.setData('application/x-tool-type', tool);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingFromToolbar(true, tool);
    
    // Create custom drag image
    const dragGhost = document.createElement('div');
    dragGhost.className = 'fixed pointer-events-none bg-primary/20 border-2 border-primary border-dashed rounded-lg flex items-center justify-center';
    dragGhost.style.width = '80px';
    dragGhost.style.height = '60px';
    dragGhost.style.zIndex = '9999';
    dragGhost.innerHTML = `<span class="text-xs font-medium text-primary">${tool}</span>`;
    document.body.appendChild(dragGhost);
    e.dataTransfer.setDragImage(dragGhost, 40, 30);
    setTimeout(() => document.body.removeChild(dragGhost), 0);
  };
  
  const handleDragEnd = () => {
    setDraggingFromToolbar(false, null);
  };

  return (
    <MotionConfig reducedMotion="user">
      {/* Centered in the canvas left of the docked Partnr panel (inset 0 when it is closed) */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50" style={rightInset ? { left: `calc(50% - ${rightInset / 2}px)` } : undefined}>
        {/* The whole pill is one dock: search circle, tools and extensions magnify together */}
        <div ref={dock.ref} {...dock.handlers} className="floating-pill rounded-full px-3 py-2.5 flex items-center gap-1.5">
          {/* Partnr Search Bar */}
          <PartnrSearchBar />

          {/* Separator */}
          <div className="w-px h-6 bg-border mx-2" />

          <div className="flex items-center gap-1.5">
          {tools.map(({ tool, icon: Icon, label, shortcut, isFileUpload, isModal, isDraggable, isArrowTool }) => {
            const isActive = (activeTool === tool && !isFileUpload && !isModal) || (isArrowTool && isArrowMode);
            return (
              <Tooltip key={tool}>
                <TooltipTrigger asChild>
              <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'relative h-10 w-10 rounded-full transition-colors duration-200',
                      isActive
                        ? 'text-primary-foreground hover:bg-transparent hover:text-primary-foreground'
                        : 'hover:bg-muted/60 text-foreground',
                      isDraggable && 'cursor-grab active:cursor-grabbing'
                    )}
                    onClick={() => handleToolClick(tool, isFileUpload, isModal, isArrowTool)}
                    disabled={isUploading}
                    draggable={isDraggable}
                    onDragStart={isDraggable ? (e) => handleDragStart(e, tool) : undefined}
                    onDragEnd={isDraggable ? handleDragEnd : undefined}
                    data-dock-item
                  >
                    {/* One pill slides between tools via the shared layoutId */}
                    {isActive && (
                      <motion.span
                        layoutId="active-tool-pill"
                        className="absolute inset-0 rounded-full bg-primary shadow-md pointer-events-none"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                    {/* The glyph magnifies, not the button, so the active pill and layout stay put */}
                    <DockGlyph>
                      {isUploading && isFileUpload ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </DockGlyph>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="mb-2">
                  {isUploading && isFileUpload ? 'Uploading...' : `${label}${shortcut ? ` (${shortcut})` : ''}${isDraggable ? ' - Drag to canvas' : ''}`}
                </TooltipContent>
              </Tooltip>
            );
          })}
          </div>

          {/* Separator */}
          <div className="w-px h-6 bg-border mx-2" />

          <ExtensionsLauncher />

        </div>
      </div>

      <input ref={imageInputRef} type="file" accept="image/*,.tif,.tiff" className="hidden" onChange={(e) => handleFileUpload(e, 'image')} />
      <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'file')} />
      
      <LinkInputModal
        open={showLinkModal}
        onOpenChange={setShowLinkModal}
        onSubmit={handleAddLink}
        isLoading={isAddingLink}
      />
    </MotionConfig>
  );
}
